/**
 * AccountingExportEditor — per-tenant QuickBooks Desktop export settings.
 *
 * ClickUp 86baxrd1j. Rendered as Settings → Accounting export (quick 261013-srg),
 * formerly a catalog card beside Products and Team (DEC-175 / quick 260814-ud3), permission MANAGE_TENANT.
 *
 * Two settings and a download. Deliberately NOT here:
 *
 *   - Any QuickBooks Class control. Class tracking was deferred 2026-09-01.
 *     The CSV emits an always-blank Class column so the office's one-time
 *     Transaction Pro mapping already covers it, but there is nothing to
 *     configure and no mapping table.
 *   - Any QBD/QBO switch. Only QBD is implemented; the stored target is shown
 *     as read-only context, not offered as a choice.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';

type LineMode = 'ITEMS' | 'FLAT_ACCOUNT';
type Cadence = 'ON_DEMAND' | 'SCHEDULED';

interface ScheduleConfig {
  readonly frequency: 'WEEKLY' | 'MONTHLY';
  readonly dayOfWeek?: number;
  readonly dayOfMonth?: number;
  readonly recipients: string[];
}

interface Settings {
  readonly exportTarget: string;
  readonly lineMode: LineMode;
  readonly cadence: Cadence;
  readonly scheduleConfig: ScheduleConfig | null;
  /** IANA zone the schedule is evaluated in — "the 1st" is a local day, not UTC. */
  readonly timezone: string;
  /**
   * When scheduled delivery was switched on. Scheduled exports never reach back
   * before this date; those invoices stay on-demand-only. Surfaced below so a
   * small first export reads as the deliberate boundary it is, rather than as
   * missing data.
   */
  readonly scheduleEnabledAt: string | null;
}

/** Local-date rendering only — UTC is never displayed (DEC-004). */
function formatLocalDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The zones the operating region actually spans (Oklahoma and Texas are Central;
 * the Permian's western edge is Mountain). A tenant already on some other zone
 * keeps it — the list is prepended with the current value rather than silently
 * snapping it to a neighbour.
 */
const TIMEZONES = [
  'America/Chicago',
  'America/Denver',
  'America/New_York',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'UTC',
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthStartIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export function AccountingExportEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const [settings, setSettings] = React.useState<Settings | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [from, setFrom] = React.useState(monthStartIso());
  const [to, setTo] = React.useState(todayIso());
  const [downloading, setDownloading] = React.useState(false);

  const load = React.useCallback(async (): Promise<void> => {
    try {
      const res = await authedFetch(opts, '/v1/tenants/accounting-export-settings');
      if (!res.ok) {
        setLoadError(`Could not load settings (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: Settings };
      setSettings(json.settings);
      setLoadError(null);
    } catch {
      setLoadError('Network error — please try again.');
    }
  }, [opts]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const patch = async (body: Record<string, unknown>): Promise<void> => {
    setSaving(true);
    try {
      const res = await authedFetch(opts, '/v1/tenants/accounting-export-settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        toast.error(
          res.status === 400
            ? 'Check your inputs — a scheduled export needs a frequency and at least one recipient.'
            : `Save failed (${String(res.status)}).`,
        );
        return;
      }
      const json = (await res.json()) as { settings: Settings };
      setSettings(json.settings);
      toast.success('Accounting export settings saved');
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  const download = async (): Promise<void> => {
    setDownloading(true);
    try {
      const res = await authedFetch(opts, `/v1/billing/accounting-export?from=${from}&to=${to}`);
      if (!res.ok) {
        toast.error(`Export failed (${String(res.status)}).`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lodex-qbd-export-${from}-to-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loadError !== null) return <p style={{ color: 'var(--color-danger)' }}>{loadError}</p>;
  if (settings === null) return <p>Loading…</p>;

  const schedule = settings.scheduleConfig;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 620 }}>
      <section>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>How lines post</h2>
        <p style={{ color: 'var(--color-text-tertiary)', fontSize: 14, marginBottom: 10 }}>
          How each invoice line should arrive in QuickBooks.
        </p>
        <label style={{ display: 'block', marginBottom: 8 }}>
          <input
            type="radio"
            name="lineMode"
            checked={settings.lineMode === 'FLAT_ACCOUNT'}
            disabled={saving}
            onChange={(): void => void patch({ accountingLineMode: 'FLAT_ACCOUNT' })}
          />{' '}
          <strong>One revenue account</strong> — each line is a dollar amount posted to a single
          account. No item names.
        </label>
        <label style={{ display: 'block' }}>
          <input
            type="radio"
            name="lineMode"
            checked={settings.lineMode === 'ITEMS'}
            disabled={saving}
            onChange={(): void => void patch({ accountingLineMode: 'ITEMS' })}
          />{' '}
          <strong>QuickBooks Items</strong> — each line carries an item name, so income is tracked
          per product/service.
        </label>
      </section>

      <section>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>When it runs</h2>
        <label style={{ display: 'block', marginBottom: 8 }}>
          <input
            type="radio"
            name="cadence"
            checked={settings.cadence === 'ON_DEMAND'}
            disabled={saving}
            onChange={(): void =>
              void patch({ accountingExportCadence: 'ON_DEMAND', accountingScheduleConfig: null })
            }
          />{' '}
          <strong>On demand</strong> — download a date range yourself, below.
        </label>
        <label style={{ display: 'block' }}>
          <input
            type="radio"
            name="cadence"
            checked={settings.cadence === 'SCHEDULED'}
            disabled={saving}
            onChange={(): void =>
              void patch({
                accountingExportCadence: 'SCHEDULED',
                accountingScheduleConfig: schedule ?? {
                  frequency: 'MONTHLY',
                  dayOfMonth: 1,
                  recipients: [],
                },
              })
            }
          />{' '}
          <strong>Scheduled</strong> — emailed automatically.
        </label>

        {settings.cadence === 'SCHEDULED' && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              border: '1px solid var(--color-border-primary)',
              borderRadius: 12,
              background: 'var(--color-bg-tertiary)',
            }}
          >
            <p style={{ fontSize: 14, color: 'var(--color-text-tertiary)', margin: 0 }}>
              {schedule?.frequency === 'WEEKLY'
                ? `Every ${WEEKDAYS[schedule.dayOfWeek ?? 0] ?? 'week'}`
                : `Day ${String(schedule?.dayOfMonth ?? 1)} of each month`}
              {schedule && schedule.recipients.length > 0
                ? ` → ${schedule.recipients.join(', ')}`
                : ' → no recipients set yet'}
            </p>
            <p
              style={{
                fontSize: 13,
                color: 'var(--color-text-tertiary)',
                marginTop: 6,
                marginBottom: 0,
              }}
            >
              Sent at 6:00 AM, {settings.timezone.replace('_', ' ')} time.{' '}
              {schedule?.frequency === 'WEEKLY'
                ? 'Each export covers the previous 7 days.'
                : 'Each export covers the previous calendar month.'}
            </p>
            {/* The schedule is meaningless without the zone it is evaluated in:
                "the 1st" in UTC begins up to six hours before it does here. */}
            <label
              style={{
                display: 'block',
                fontSize: 13,
                color: 'var(--color-text-tertiary)',
                marginTop: 8,
              }}
            >
              Time zone
              <br />
              <select
                value={settings.timezone}
                disabled={saving}
                onChange={(e): void => void patch({ timezone: e.target.value })}
                style={{ marginTop: 4 }}
              >
                {(TIMEZONES.includes(settings.timezone)
                  ? TIMEZONES
                  : [settings.timezone, ...TIMEZONES]
                ).map((tz) => (
                  <option key={tz} value={tz}>
                    {tz.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </label>
            {/* The watermark, stated plainly. Without this, a hauler who enables
                scheduling mid-month sees a small first export and reads it as
                data loss rather than as the deliberate boundary that keeps
                already-imported invoices out of QuickBooks a second time. */}
            {settings.scheduleEnabledAt !== null && (
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-warning)',
                  background: 'rgba(246, 182, 94, 0.12)',
                  border: '1px solid rgba(246, 182, 94, 0.35)',
                  borderRadius: 12,
                  padding: 8,
                  marginTop: 8,
                  marginBottom: 0,
                }}
              >
                Scheduled exports cover invoices dated {formatLocalDate(settings.scheduleEnabledAt)}{' '}
                onward — the day scheduled delivery was turned on. Anything earlier stays available
                through the on-demand download below, so it is never sent twice.
              </p>
            )}
          </div>
        )}
      </section>

      <section>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Download a date range</h2>
        <p style={{ color: 'var(--color-text-tertiary)', fontSize: 14, marginBottom: 10 }}>
          Produces a CSV for Transaction Pro Importer. All lines of an invoice share one invoice
          number so they import as a single invoice.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label>
            From
            <br />
            <input type="date" value={from} onChange={(e): void => setFrom(e.target.value)} />
          </label>
          <label>
            To
            <br />
            <input type="date" value={to} onChange={(e): void => setTo(e.target.value)} />
          </label>
          <button type="button" disabled={downloading} onClick={(): void => void download()}>
            {downloading ? 'Preparing…' : 'Download CSV'}
          </button>
        </div>
      </section>

      <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
        Exporting for QuickBooks Desktop ({settings.exportTarget}). QuickBooks Online is not
        available yet.
      </p>
    </div>
  );
}
